import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import api from '../api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Megaphone, AlertTriangle, Info } from 'lucide-react';

/**
 * NoticePopup
 * Automatically fetches unacknowledged active notices for the logged-in user
 * and shows them one-by-one as a blocking dialog.
 * Each notice is marked as "read" when the user clicks "Got it".
 */
const NoticePopup = () => {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [acking, setAcking] = useState(false);

  const fetchActive = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get('/notices/active');
      setQueue(r.data || []);
    } catch {}
  }, [user]);

  useEffect(() => {
    fetchActive();
  }, [fetchActive]);

  const current = queue[0];

  const acknowledge = async () => {
    if (!current || acking) return;
    setAcking(true);
    try {
      await api.post(`/notices/${current.id}/acknowledge`);
      setQueue(q => q.slice(1));
    } catch {} finally {
      setAcking(false);
    }
  };

  if (!current) return null;

  const priorityMeta = {
    urgent: { icon: AlertTriangle, bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700', badge: 'bg-red-600 text-white', label: 'URGENT' },
    important: { icon: Megaphone, bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', badge: 'bg-amber-500 text-white', label: 'IMPORTANT' },
    info: { icon: Info, bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-600 text-white', label: 'NOTICE' },
  }[current.priority || 'info'];
  const Icon = priorityMeta.icon;

  return (
    <Dialog open onOpenChange={() => { /* forced modal - no close on outside click */ }}>
      <DialogContent className="max-w-xl p-0 overflow-hidden" data-testid="notice-popup" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <div className={`${priorityMeta.bg} ${priorityMeta.border} border-b-2 px-6 py-4 flex items-start gap-3`}>
          <div className={`${priorityMeta.badge} w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0`}>
            <Icon className="w-5 h-5" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${priorityMeta.badge}`}>{priorityMeta.label}</span>
              <span className="text-[10px] text-slate-500">from {current.created_by_email}</span>
            </div>
            <DialogHeader>
              <DialogTitle className={`text-lg font-bold ${priorityMeta.text}`} data-testid="notice-title">{current.title}</DialogTitle>
            </DialogHeader>
          </div>
        </div>

        <div className="px-6 py-5">
          <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid="notice-body">
            {current.message}
          </div>
          <p className="text-[11px] text-slate-400 mt-4">
            Posted on {new Date(current.created_at).toLocaleString('en-IN')} by {current.created_by_name || current.created_by_email}
          </p>
        </div>

        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <p className="text-xs text-slate-500">
            {queue.length > 1 ? `${queue.length - 1} more notice${queue.length - 1 === 1 ? '' : 's'} after this` : 'This is your last pending notice'}
          </p>
          <Button onClick={acknowledge} disabled={acking} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="acknowledge-notice-btn">
            {acking ? 'Marking...' : 'Got it'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NoticePopup;
